import React from 'react';
import classNames from 'classnames';
import { ValidationSummary } from 'utils/ValidationUtils';
import { useHoverValidation } from './hooks/useHoverValidation';
import { ValidationError } from './ValidationError';
import {
  getValidationUnderlineClass,
  getValidationOutlineClass,
  getHasValidationErrorClass,
} from './helpers/ValidationCssHelpers';
import { DataAttributes } from 'utils/DataAttributeUtils';
// import './ValidationWrapper.less'

interface IValidationWrapperProps extends ValidationWrapperCommonProps {
  children: React.ReactNode;
  validation?: ValidationSummary;
  elementClassName?: string;
  calloutClassName?: string;
  /**
   * Defaults to `true` pass `false` to avoid rendering tooltips, e.g. if a dropdown is open
   */
  shouldShow?: boolean;
}

export type ValidationBorderType = 'outline' | 'underline' | 'none';

/**
 * Helper interface for components which wrap [[ValidationWrapper]] to help
 * provide the [[ValidationSummary]].
 */
export interface ValidationWrapperCommonProps extends DataAttributes {
  borderType?: ValidationBorderType;
}

const getClassName = (props: IValidationWrapperProps, className?: string) => {
  return classNames(className, getHasValidationErrorClass(props.validation), {
    // apply outline styles by default
    [getValidationOutlineClass(props.validation)]: props.borderType === 'outline' || props.borderType == null,
    [getValidationUnderlineClass(props.validation)]: props.borderType === 'underline',
  });
};

/**
 * Wrapper which adds a validation behavior to its _first_ child element.
 *
 * If validation props is undefined, this is a NOOP.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ValidationWrapper(props: IValidationWrapperProps) {
  const { validation, elementClassName: className, shouldShow = true } = props;
  const { inputRef, onShowValidation, onHideValidation, valInputAttributes, show, toolTipId } = useHoverValidation(
    validation,
    shouldShow
  );

  if (!validation) {
    return <>{props.children}</> || null;
  }

  const childList = React.Children.toArray(props.children);
  const fieldChild = childList.shift() as JSX.Element;
  const validationProps: any = {
    // add handlers:
    onMouseOver: (e: React.MouseEvent<HTMLElement>) => {
      onShowValidation(e);
      fieldChild.props.onMouseOver?.(e);
    },
    onMouseOut: (e: React.MouseEvent<HTMLElement>) => {
      onHideValidation();
      fieldChild.props.onMouseOut?.(e);
    },
    onFocus: (e: React.FocusEvent<HTMLElement>) => {
      onShowValidation(e);
      fieldChild.props.onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent<HTMLElement>) => {
      onHideValidation();
      fieldChild.props.onBlur?.(e);
    },
    // pass on important props:
    className: className || getClassName(props, fieldChild.props.className),
    ...valInputAttributes,
  };

  // pass on data attributes, but don't overwrite them if they aren't passed here
  if (props['data-label']) {
    validationProps['data-label'] = props['data-label'];
  }
  if (props['data-path']) {
    validationProps['data-path'] = props['data-path'];
  }
  if (props['data-testid']) {
    validationProps['data-testid'] = props['data-testid'];
  }

  const validationAdded = React.cloneElement(fieldChild, validationProps);

  return (
    <>
      {validationAdded}
      {childList}
      {validation && <ValidationError {...validation} sourceRef={inputRef} id={toolTipId} isOpen={show} />}
    </>
  );
}
